import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { productCategories } from '@/mocks/products';

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || i18n.language || 'en';

  useEffect(() => {
    if (lang && ['en', 'fr', 'da', 'de'].includes(lang)) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Page Header */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-12 bg-background-100">
        <div className="container-main">
          <div className="max-w-3xl">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">
              All Products
            </h1>
            <p className="text-foreground-600 text-lg leading-relaxed">
              Browse the full AllChinaBuy product catalog. Click any item to view details and purchase on kaspreadsheet.com.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid by Category */}
      <section className="section-padding">
        <div className="container-main">
          {productCategories.map((category) => (
            <div key={category.id} className="mb-16 md:mb-20 last:mb-0">
              {/* Category Header */}
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div className="flex items-center gap-3">
                  <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900">
                    {category.name}
                  </h2>
                  <span className="text-sm text-foreground-400 bg-background-100 px-3 py-1 rounded-full">
                    {category.products.length} items
                  </span>
                </div>
                <a
                  href={category.categoryUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-50 text-primary-600 text-sm font-medium hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  View All in {category.name}
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
              </div>

              {/* Product Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {category.products.map((product) => (
                  <a
                    key={product.id}
                    href={product.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="group/card block"
                  >
                    <div className="relative aspect-square rounded-xl overflow-hidden bg-background-100 mb-3 border border-background-200/70">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                        width={240}
                        height={240}
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover/card:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-xs font-medium text-foreground-800 shadow-lg">
                          View on kaspreadsheet
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover/card:text-primary-600 transition-colors">
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-primary-600">
                          ¥{product.priceCNY}
                        </span>
                        <span className="text-xs text-foreground-400">
                          ≈ ${product.priceUSD}
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}